import { createApi } from '@reduxjs/toolkit/query/react';
import { customFetchBase } from './helpers/customFetchBase.js';
import { type BaseBug, type Bug, type BaseKbArticle, type KbArticle } from '@soulhx/fs-common';
import { providesList } from './helpers/rtk-helpers.js';

const KB_TYPES = ['kb'];

const TAG_TYPES = KB_TYPES.map((kt) => `kbarticles-${kt}`);

export interface NewKbRequest {
  article: BaseKbArticle;
  type: string;
}

export interface GetKbArticlesRequest {
  page?: number;
  type: string;
}

export interface GetKbArticleByIdRequest {
  id: string;
  type: string;
}

export interface SearchKbArticlesRequest {
  page?: number;
  searchText?: string;
  type: string;
}

export interface DeleteArticleResponse {
  success: boolean;
}

export interface DeleteKbArticleRequest {
  id: string;
  type: string;
}

export interface GetBugsRequest {
  showFixed: boolean;
  page?: number;
}

export interface SearchBugsRequest {
  showFixed: boolean;
  page?: number;
  searchText?: string;
}

export interface DeleteBugResponse {
  success: boolean;
}

export const kbArticlesApi = createApi({
  reducerPath: 'kbArticles',
  baseQuery: customFetchBase,
  tagTypes: TAG_TYPES.concat(['bugs']),
  endpoints: (builder) => ({
    newKbArticle: builder.mutation<KbArticle, NewKbRequest>({
      query: (body) => ({
        url: `/comms/kb`,
        body: body.article,
        method: 'POST',
      }),
      invalidatesTags: (article, error, request) => [{ type: `kbarticles-${request.type}`, id: 'LIST' }],
    }),
    getAllKbArticles: builder.query<KbArticle[], GetKbArticlesRequest>({
      query: (params) => (params.page ? `/comms/kb/query/${params.type}?p=${params.page}` : '/comms/kb/query'),
      providesTags: (articles, error, request) => providesList(articles, `kbarticles-${request.type}`),
    }),
    getKbArticleById: builder.query<KbArticle, GetKbArticleByIdRequest>({
      query: (request) => `/comms/kb/${request.id}`,
      providesTags: (article, error, request) =>
        article ? providesList([article], `kbarticles-${request.type}`, { returnList: false }) : [],
    }),
    searchKbArticles: builder.mutation<KbArticle[], SearchKbArticlesRequest>({
      query: (request) => ({
        url: `/comms/kb/query/${request.type}${request.page ? '?p=' + request.page : ''}`,
        body: { searchText: request.searchText },
        method: 'POST',
      }),
    }),
    updateKbArticle: builder.mutation<KbArticle, KbArticle>({
      query: (item) => ({
        url: `/comms/kb/${item.id}`,
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: (article, error, request) => [{ type: `kbarticles-${request.type}`, id: request.id }],
    }),
    deleteKbArticle: builder.mutation<DeleteArticleResponse, DeleteKbArticleRequest>({
      query: (request) => ({
        url: `/comms/kb/${request.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (deleteArticleResponse, error, request) => [
        { type: `kbarticles-${request.type}`, id: request.id },
        { type: `kbarticles-${request.type}`, id: 'LIST' },
      ],
    }),

    newBug: builder.mutation<Bug, BaseBug>({
      query: (bug) => ({
        url: `/comms/bug`,
        body: bug,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'bugs', id: 'LIST' }],
    }),
    getAllBugs: builder.query<Bug[], GetBugsRequest>({
      query: (params) =>
        params.page
          ? `/comms/bug/query/?p=${params.page}&showFixed=${params.showFixed}`
          : `/comms/bug/query?showFixed=${params.showFixed}`,
      providesTags: (result) => providesList(result, 'bugs'),
    }),
    getBugById: builder.query<Bug, string>({
      query: (id) => `/comms/bug/${id}`,
      providesTags: (result) => (result ? providesList([result], 'bugs', { returnList: false }) : []),
    }),
    searchBugs: builder.mutation<Bug[], SearchBugsRequest>({
      query: (request) => ({
        url: `/comms/bug/query/?showFixed=${request.showFixed}${request.page ? '&p=' + request.page : ''}`,
        body: { searchText: request.searchText },
        method: 'POST',
      }),
    }),
    updateBug: builder.mutation<Bug, Bug>({
      query: (item) => ({
        url: `/comms/bug/${item.id}`,
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: (result, error, item) => [{ type: 'bugs', id: item.id }],
    }),
    deleteBug: builder.mutation<DeleteBugResponse, string>({
      query: (id) => ({
        url: `/comms/bug/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'bugs', id },
        { type: 'bugs', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useDeleteKbArticleMutation,
  useGetAllKbArticlesQuery,
  useGetKbArticleByIdQuery,
  useLazyGetAllKbArticlesQuery,
  useLazyGetKbArticleByIdQuery,
  useNewKbArticleMutation,
  useSearchKbArticlesMutation,
  useUpdateKbArticleMutation,
  useDeleteBugMutation,
  useGetAllBugsQuery,
  useGetBugByIdQuery,
  useLazyGetAllBugsQuery,
  useLazyGetBugByIdQuery,
  useNewBugMutation,
  useSearchBugsMutation,
  useUpdateBugMutation,
} = kbArticlesApi;
